import {useCallback, useMemo, useRef, useState} from 'react'
import {SorterResult} from 'antd/es/table/interface'
import {readReportRows, parseCSVRow} from './reportCsv'

export interface ReportPollingApi {
    get: (id: string, size?: number, page?: number) => Promise<Response>
    info: (id: string) => Promise<any>
    csv: (id: string) => Promise<Response>
}

export type ReportPollingState = {
    loading: boolean
    error?: string
    headers?: string[]
    data?: string[][]
    totals?: { [key: string]: number }
    reportId?: string
    totalLines?: number
    currentPage: number
    pageSize: number
}

type UseReportPollingParams = {
    api: ReportPollingApi
    downloadCSVData: (filename: string, blob: Blob) => void
    requestReport: (sorter?: SorterResult<any>) => Promise<string | undefined>
    pollMs?: number
}

export const useReportPolling = (params: UseReportPollingParams) => {
    const {api, downloadCSVData, requestReport, pollMs = 500} = params

    const intervalRef = useRef<any>(null)

    const clearPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }, [])

    const [state, setState] = useState<ReportPollingState>({
        loading: false,
        error: undefined,
        headers: undefined,
        data: undefined,
        totals: undefined,
        reportId: undefined,
        totalLines: 0,
        currentPage: 0,
        pageSize: 0,
    })

    const setLoading = useCallback((loading: boolean) => {
        setState((s) => ({...s, loading}))
    }, [])

    const setError = useCallback((error?: string) => {
        setState((s) => ({...s, error}))
    }, [])

    const parseAndSetFromRows = useCallback((rows: string[]) => {
        const hdrs = rows[0].split(',')
        const body = rows.slice(1, -1)

        setState((s) => ({
            ...s,
            headers: hdrs,
            data: body.map(parseCSVRow),
        }))

        return {hdrs, bodyLength: body.length}
    }, [])

    const runReport = useCallback(
        async (sorter: SorterResult<any> | undefined, clearPrevious: boolean = false) => {
            clearPolling()
            setLoading(true)

            if (clearPrevious) {
                setState((s) => ({
                    ...s,
                    data: undefined,
                    totals: undefined,
                    totalLines: 0,
                    currentPage: 0,
                    pageSize: 0,
                    reportId: undefined,
                }))
            }

            const uuid = await requestReport(sorter)
            if (!uuid) {
                setLoading(false)
                return
            }

            setState((s) => ({...s, reportId: uuid}))

            intervalRef.current = setInterval(async () => {
                try {
                    const ready = await api.get(uuid)
                    clearPolling()

                    const rows = await readReportRows(ready)
                    if (rows == null) {
                        setLoading(false)
                        return
                    }

                    const {bodyLength} = parseAndSetFromRows(rows)
                    setLoading(false)

                    if (bodyLength > 1) {
                        const reportInfo = await api.info(uuid)
                        const reportBean = reportInfo.data

                        setState((s) => ({
                            ...s,
                            totals: reportBean?.totalValues,
                            totalLines: reportBean?.totalLines,
                            currentPage: 1,
                            pageSize: 50,
                        }))
                    } else {
                        setState((s) => ({
                            ...s,
                            totals: undefined,
                            totalLines: 0,
                            currentPage: 0,
                            pageSize: 0,
                        }))
                    }
                } catch (response) {
                    if (typeof response === 'object') {
                        // @ts-ignore
                        if (response.status !== 425) {
                            setLoading(false)
                            clearPolling()
                        }
                    }
                }
            }, pollMs)
        },
        [api, clearPolling, parseAndSetFromRows, pollMs, requestReport, setLoading],
    )

    const loadPage = useCallback(
        async (page: number, size: number) => {
            const reportId = state.reportId ?? ''
            if (!reportId) return

            try {
                const ready = await api.get(reportId, size, page)
                const rows = await readReportRows(ready)
                if (rows == null) return

                parseAndSetFromRows(rows)
                setLoading(false)

                setState((s) => ({...s, currentPage: page, pageSize: size}))
            } catch (e) {
                setLoading(false)
            }
        },
        [api, parseAndSetFromRows, setLoading, state.reportId],
    )

    const downloadCsv = useCallback(async () => {
        clearPolling()

        if (state.reportId) {
            const response = await api.csv(state.reportId)
            const blob = await response.blob()
            downloadCSVData('report.csv', blob)
            return
        }

        setLoading(true)
        const uuid = await requestReport(undefined)
        if (!uuid) {
            setLoading(false)
            return
        }

        setState((s) => ({...s, reportId: uuid}))

        intervalRef.current = setInterval(async () => {
            try {
                const ready = await api.csv(uuid)
                clearPolling()
                setLoading(false)

                const blob = await ready.blob()
                downloadCSVData('report.csv', blob)
            } catch (response) {
                if (typeof response === 'object') {
                    // @ts-ignore
                    if (response.status !== 425) {
                        setLoading(false)
                        clearPolling()
                    }
                }
            }
        }, pollMs)
    }, [api, clearPolling, downloadCSVData, pollMs, requestReport, setLoading, state.reportId])

    const reset = useCallback(() => {
        clearPolling()
        setState({
            loading: false,
            error: undefined,
            headers: undefined,
            data: undefined,
            totals: undefined,
            reportId: undefined,
            totalLines: 0,
            currentPage: 0,
            pageSize: 0,
        })
    }, [clearPolling])

    return useMemo(
        () => ({
            runReport,
            downloadCsv,
            loadPage,
            state,
            setError,
            reset,
        }),
        [downloadCsv, loadPage, reset, runReport, setError, state],
    )
}


// WEBPACK FOOTER //
// ./src/component/app/report/useReportPolling.ts